/**
 * useRitualData - Hook for managing ritual data persistence
 * Handles loading/saving routines, logs, and streak calculation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, differenceInDays, parseISO, isYesterday, isToday } from 'date-fns';
import { DEFAULT_MORNING_ACTIVITIES, DEFAULT_EVENING_ACTIVITIES, RoutineType } from '@/components/rituals/types';

export interface RitualLog {
  id: string;
  routine_id: string;
  log_date: string;
  mood_before: number;
  mood_after: number;
  energy_before: number;
  energy_after: number;
  activities_completed: string[];
  notes: string | null;
  started_at: string;
  completed_at: string;
}

export interface UserRoutine {
  id: string;
  routine_type: string;
  is_active: boolean;
}

export interface TomorrowAction {
  id: string;
  action_date: string;
  action_1: string;
  action_2: string;
  action_3: string;
  action_1_completed: boolean;
  action_2_completed: boolean;
  action_3_completed: boolean;
  reviewed_at: string | null;
}

export interface RitualStreak {
  currentStreak: number;
  longestStreak: number;
  totalCompletedDays: number;
  lastCompletedDate: string | null;
}

// Get or create default routines for user
export function useUserRoutines() {
  return useQuery({
    queryKey: ['user-routines'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Check if user has routines
      const { data: existingRoutines, error } = await supabase
        .from('user_ritual_routines')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching routines:', error);
        return [];
      }

      // If no routines exist, create defaults
      if (!existingRoutines || existingRoutines.length === 0) {
        const defaultRoutines = [
          { user_id: user.id, routine_type: 'morning', is_active: true },
          { user_id: user.id, routine_type: 'evening', is_active: true }
        ];

        const { data: createdRoutines, error: createError } = await supabase
          .from('user_ritual_routines')
          .insert(defaultRoutines)
          .select();

        if (createError) {
          console.error('Error creating routines:', createError);
          return [];
        }

        // Create default items for each routine
        for (const routine of createdRoutines || []) {
          const activities = routine.routine_type === 'morning' 
            ? DEFAULT_MORNING_ACTIVITIES 
            : DEFAULT_EVENING_ACTIVITIES;

          const items = activities.map((activity, index) => ({
            routine_id: routine.id,
            activity_id: activity.id,
            sort_order: index,
            is_active: true
          }));

          await supabase.from('user_ritual_items').insert(items as any);
        }

        return createdRoutines as UserRoutine[];
      }

      return existingRoutines as UserRoutine[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Get today's ritual completion status
export function useTodayRitualStatus() {
  const { data: routines } = useUserRoutines();

  return useQuery({
    queryKey: ['today-ritual-status'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { morning: false, evening: false };

      const today = format(new Date(), 'yyyy-MM-dd');

      const { data: logs } = await supabase
        .from('daily_ritual_logs')
        .select('routine_id')
        .eq('user_id', user.id)
        .eq('log_date', today);

      if (!logs || !routines) return { morning: false, evening: false };

      const completedRoutineIds = new Set(logs.map(l => l.routine_id));
      
      const morningRoutine = routines.find(r => r.routine_type === 'morning');
      const eveningRoutine = routines.find(r => r.routine_type === 'evening');

      return {
        morning: morningRoutine ? completedRoutineIds.has(morningRoutine.id) : false,
        evening: eveningRoutine ? completedRoutineIds.has(eveningRoutine.id) : false,
      };
    },
    enabled: !!routines,
    staleTime: 60 * 1000, // 1 minute
  });
}

// Calculate ritual streak
export function useRitualStreak() {
  return useQuery<RitualStreak>({
    queryKey: ['ritual-streak'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { currentStreak: 0, longestStreak: 0, totalCompletedDays: 0, lastCompletedDate: null };

      // Get all logs for last 90 days
      const startDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');
      
      const { data: logs } = await supabase
        .from('daily_ritual_logs')
        .select('log_date')
        .eq('user_id', user.id)
        .gte('log_date', startDate)
        .order('log_date', { ascending: false });

      if (!logs || logs.length === 0) {
        return { currentStreak: 0, longestStreak: 0, totalCompletedDays: 0, lastCompletedDate: null };
      }

      // Get unique dates
      const uniqueDates = [...new Set(logs.map(l => l.log_date))].sort().reverse();
      const totalCompletedDays = uniqueDates.length;
      const lastCompletedDate = uniqueDates[0];

      // Calculate current streak
      let currentStreak = 0;
      const today = format(new Date(), 'yyyy-MM-dd');
      const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
      
      // Check if the streak is still active (completed today or yesterday)
      if (lastCompletedDate === today || lastCompletedDate === yesterday) {
        for (let i = 0; i < uniqueDates.length; i++) {
          const expectedDate = format(subDays(new Date(), i + (lastCompletedDate === yesterday ? 1 : 0)), 'yyyy-MM-dd');
          if (uniqueDates.includes(expectedDate)) {
            currentStreak++;
          } else {
            break;
          }
        }
      }

      // Calculate longest streak
      let longestStreak = 0;
      let tempStreak = 1;
      const sortedDates = [...uniqueDates].sort();
      
      for (let i = 1; i < sortedDates.length; i++) {
        const prevDate = parseISO(sortedDates[i - 1]);
        const currDate = parseISO(sortedDates[i]);
        const dayDiff = differenceInDays(currDate, prevDate);
        
        if (dayDiff === 1) {
          tempStreak++;
        } else {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
        }
      }
      longestStreak = Math.max(longestStreak, tempStreak, currentStreak);

      return { currentStreak, longestStreak, totalCompletedDays, lastCompletedDate };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Log completed ritual
export function useLogRitual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      routineType: RoutineType;
      moodBefore: number;
      moodAfter: number;
      activitiesCompleted: string[];
      notes?: string;
      tomorrowActions?: {
        action1: string;
        action2: string;
        action3: string;
        futureConnection?: string;
      };
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch routines directly inside mutation to avoid race condition
      const { data: routines, error: routinesError } = await supabase
        .from('user_ritual_routines')
        .select('*')
        .eq('user_id', user.id)
        .eq('routine_type', data.routineType)
        .maybeSingle();

      if (routinesError) {
        console.error('Error fetching routine:', routinesError);
        throw new Error('Could not find routine');
      }

      let routine = routines;

      // If no routine exists, create it
      if (!routine) {
        const { data: newRoutine, error: createError } = await supabase
          .from('user_ritual_routines')
          .insert({
            user_id: user.id,
            routine_type: data.routineType,
            is_active: true
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating routine:', createError);
          throw new Error('Could not create routine');
        }
        routine = newRoutine;
      }

      const today = format(new Date(), 'yyyy-MM-dd');
      const now = new Date().toISOString();

      // Insert or update ritual log (upsert to handle duplicate saves)
      const { error: logError } = await supabase
        .from('daily_ritual_logs')
        .upsert({
          user_id: user.id,
          routine_id: routine.id,
          log_date: today,
          mood_before: data.moodBefore,
          mood_after: data.moodAfter,
          energy_before: 5, // Default
          energy_after: 5, // Default
          activities_completed: data.activitiesCompleted,
          notes: data.notes || null,
          started_at: now,
          completed_at: now,
        }, {
          onConflict: 'user_id,routine_id,log_date'
        });

      if (logError) throw logError;

      // If evening ritual with tomorrow actions, save those too
      if (data.routineType === 'evening' && data.tomorrowActions) {
        const tomorrow = format(subDays(new Date(), -1), 'yyyy-MM-dd');
        
        const { error: actionError } = await supabase
          .from('tomorrow_actions')
          .upsert({
            user_id: user.id,
            action_date: tomorrow,
            action_1: data.tomorrowActions.action1,
            action_2: data.tomorrowActions.action2,
            action_3: data.tomorrowActions.action3,
            future_self_connection: data.tomorrowActions.futureConnection || null,
          } as any, {
            onConflict: 'user_id,action_date'
          });

        if (actionError) console.error('Error saving tomorrow actions:', actionError);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-routines'] });
      queryClient.invalidateQueries({ queryKey: ['today-ritual-status'] });
      queryClient.invalidateQueries({ queryKey: ['ritual-streak'] });
      queryClient.invalidateQueries({ queryKey: ['ritual-ai-insights'] });
      queryClient.invalidateQueries({ queryKey: ['ritual-whoop-correlation'] });
    },
  });
}

// Get yesterday's actions for review
export function useYesterdayActions() {
  return useQuery<TomorrowAction | null>({
    queryKey: ['yesterday-actions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const today = format(new Date(), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('tomorrow_actions')
        .select('*')
        .eq('user_id', user.id)
        .eq('action_date', today)
        .is('reviewed_at', null)
        .maybeSingle();

      if (error) {
        console.error('Error fetching yesterday actions:', error);
        return null;
      }

      return data as TomorrowAction | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Review yesterday's actions
export function useReviewActions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      actionId: string;
      action1Completed: boolean;
      action2Completed: boolean;
      action3Completed: boolean;
      barriers?: string;
    }) => {
      const { error } = await supabase
        .from('tomorrow_actions')
        .update({
          action_1_completed: data.action1Completed,
          action_2_completed: data.action2Completed,
          action_3_completed: data.action3Completed,
          barriers_encountered: data.barriers || null,
          reviewed_at: new Date().toISOString(),
        } as any)
        .eq('id', data.actionId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yesterday-actions'] });
    },
  });
}

export default useRitualStreak;
